import React from "react";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InputFile from "../GeneralComponents/InputFile";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import FileDownload from "@mui/icons-material/FileDownload";
import { Button, InputAdornment } from "@mui/material";

import { parseBoldText, toSnakeCase } from "../utils/general_utils";

// dayjs para parsear strings y aceptar también dayjs directamente
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const toDayjs = (value, formats) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;
  const d = dayjs(value, formats, "es", true);
  return d.isValid() ? d : null;
};

export default function PeriodoGraciaHiddenPreview({
  formData,
  text = "",
  evidence = null,
  children = null,
  id = null,
  embedded = false, 
}) {
  const datos = formData ?? {};

  const dtOcurrencia = toDayjs(
    datos.especifica_fecha_hora_ocurrencia,
    ["YYYY-MM-DDTHH:mm:ss", "YYYY/MM/DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss"]
  );
  const dtResolucion = toDayjs(
    datos.especifica_fecha_hora_resolucion,
    ["YYYY-MM-DDTHH:mm:ss", "YYYY/MM/DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss"]
  );

  const titulo = "PERIODO DE GRACIA";

  const mainContent = (
    <Box sx={{ width: "100%" }}>
      {/* CABECERA */}
      <Typography
        variant="h1"
        color="inherit"
        noWrap
        sx={{ flexGrow: 1, textAlign: "center", fontWeight: 700 }}
      >
        {titulo}
      </Typography>

      <Stack alignItems="left" justifyContent="center" spacing={2}>
        <Box
          justifyContent="center"
          sx={{ display: "flex", margin: "0.2rem!important" }}
        >
          <Stack>
            {(() => {
              const hasId = id != null && String(id) !== "";
              const sizes = hasId
                ? { label: ".8875rem", value: ".8875rem" }
                : { label: ".6875rem", value: ".6875rem" };

              return (
                <>
                  <Typography
                    component="div"
                    color="inherit"
                    sx={{
                      textAlign: "center",
                      justifyContent: "center",
                      py: 0,
                      mt: 0,
                      fontSize: sizes.label,
                    }}
                  >
                    N° folio:{" "}
                    <Typography
                      component="span"
                      sx={{
                        pl: "1rem",
                        mt: 0,
                        fontSize: sizes.value,
                        color: hasId ? undefined : "#FFF",
                      }}
                    >
                      {String(id ?? "").padStart(8, "0")}
                    </Typography>
                  </Typography>

                  <Typography
                    component="div"
                    color="inherit"
                    sx={{
                      textAlign: "center",
                      justifyContent: "center",
                      py: 0,
                      mt: 0,
                      fontSize: sizes.label,
                    }}
                  >
                    Fecha de creación:{" "}
                    <Typography
                      component="span"
                      sx={{
                        pl: "1rem",
                        mt: 0,
                        fontSize: sizes.value,
                      }}
                    >
                      {(datos.fecha_creacion || new Date().toISOString()).slice(
                        0,
                        10
                      )}
                    </Typography>
                  </Typography>
                </>
              );
            })()}
          </Stack>
        </Box>

        <Box
          justifyContent="center"
          sx={{ maxWidth: "100%", margin: "0 auto", display: "flex" }}
        >
          <Typography
            variant="body"
            align="center"
            sx={{ marginBottom: "2rem" }}
          >
            {parseBoldText(text)}
          </Typography>
        </Box>
      </Stack>

      {/* CONTENIDO PRINCIPAL */}
      <form id="preiodo_gracia">
        <Stack spacing={4} sx={{ marginBottom: "2rem" }}>
          {/* DATOS DEL EMISOR */}
          <Typography variant="h3" color="inherit" noWrap sx={{ flexGrow: 1 }}>
            DATOS DEL EMISOR
          </Typography>

          <Grid container spacing={2}>
            <Grid size={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  test_id={toSnakeCase("rut_emisor")}
                  disabled
                  required
                  name="rut"
                  label="Rut Emisor"
                  size="small"
                  value={datos.rut_emisor}
                />
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  test_id={toSnakeCase("name_emisor")}
                  disabled
                  required
                  name="nombre"
                  label="Nombre y Apellido"
                  size="small"
                  value={datos.name_emisor}
                />
              </FormControl>
            </Grid>
          </Grid>

          {/* DATOS BASE PERIODO DE GRACIA */}
          <Typography variant="h3" color="inherit" noWrap sx={{ flexGrow: 1 }}>
            DATOS BASE DE PERIODO GRACIA
          </Typography>

          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  test_id={toSnakeCase("especifica_codigo_evento")}
                  disabled
                  label="Código de evento"
                  size="small"
                  value={
                    datos.especifica_codigo_evento === "19"
                      ? "19 - Periodo de vacaciones"
                      : datos.especifica_codigo_evento || ""
                  }
                />
              </FormControl>
            </Grid>
          </Grid>

          {/* DATOS DEL OPERADOR / ZONAS */}
          <Typography variant="h3" color="inherit" noWrap sx={{ flexGrow: 1 }}>
            DATOS DEL OPERADOR
          </Typography>

          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  test_id={toSnakeCase("operator_name")}
                  disabled
                  required
                  name="operator_name"
                  label="Nombre Operador"
                  size="small"
                  value={datos.operator_name}
                />
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TableContainer component={Paper} sx={{ mb: 2, mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "80px" }}>
                        <IconButton test_id={toSnakeCase("delete")} disabled>
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ width: "80px" }}>Id Zona</TableCell>
                      <TableCell>RBDs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(datos.zonas) && datos.zonas.length > 0 ? (
                      datos.zonas.map((zona, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <IconButton
                              test_id={toSnakeCase("delete_" + index)}
                              disabled
                            >
                              <RemoveCircleOutlineIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle" }}>
                            {zona.id_zona}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "middle" }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {Array.isArray(zona.rbd_list) &&
                              zona.rbd_list.length > 0
                                ? zona.rbd_list.map((rbd, ri) => (
                                    <Chip
                                      key={`${zona.id_zona}-${ri}`}
                                      label={String(rbd)}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))
                                : null}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No hay zonas agregadas
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          {/* DETALLE DEL PERIODO DE GRACIA */}
          <Typography variant="h3" color="inherit" noWrap sx={{}}>
            DETALLE DEL PERIODO DE GRACIA
          </Typography>

          <Grid container spacing={2}>
            <Grid size={6}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={"es"}
              >
                <DateTimePicker
                  test_id={toSnakeCase("especifica_fecha_hora_ocurrencia")}
                  disabled
                  sx={{ width: "100%" }}
                  label="Fecha y hora de ocurrencia"
                  value={dtOcurrencia}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                  disableFuture
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={6}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={"es"}
              >
                <DateTimePicker
                  test_id={toSnakeCase("especifica_fecha_hora_resolucion")}
                  disabled
                  sx={{ width: "100%" }}
                  label="Fecha y hora de resolución"
                  value={dtResolucion}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <FormControl sx={{ width: "100%", maxWidth: "100%" }}>
            <TextField
              test_id={toSnakeCase("especifica_descripcion_falla")}
              disabled
              multiline
              minRows={4}
              maxRows={30}
              id="outlined-required"
              label="Descripción de falla"
              placeholder="Descripción de falla"
              size="small"
              value={datos.especifica_descripcion_falla}
            />
          </FormControl>

          {/* EVIDENCIA */}
          {evidence ? (
            <Box>
              <InputFile
                onFileSelect={() => {}}
                initialFile={evidence}
                fileType="pdf"
                placeholder="Subir evidencia (formato PDF)"
              />
              <Typography
                variant="small"
                align="left"
                sx={{ marginBottom: "5px", color: "#111" }}
              ></Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid size={8}>
                <TextField
                  test_id={toSnakeCase("evidence")}
                  disabled
                  size="small"
                  variant="outlined"
                  placeholder="Subir evidencia (formato PDF)"
                  value={
                    Array.isArray(datos.evidence)
                      ? datos.evidence.join(", ")
                      : datos.evidence || ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ marginLeft: "10px" }}
                      >
                        <ImageSearchIcon sx={{ color: "black" }} />
                      </InputAdornment>
                    ),
                    readOnly: true,
                  }}
                  sx={{ width: "100%", maxWidth: "100%" }}
                  readOnly
                />
              </Grid>
              <Grid size={4}>
                <Button
                  test_id={toSnakeCase("Descargar evidencia")}
                  fullWidth
                  variant="text"
                  disabled={
                    Array.isArray(datos.evidence) &&
                    datos.evidence.length > 0
                      ? false
                      : true
                  }
                  color="primary"
                  onClick={() => window.open(datos.evidence?.[0], "_blank")}
                  startIcon={<FileDownload />}
                >
                  Descargar evidencia
                </Button>
              </Grid>
            </Grid>
          )}
        </Stack>
      </form>
    </Box>
  );

  return (
    <>
      {embedded ? (
        // Versión embebida (por ejemplo en un drawer)
        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflowY: "auto",
            p: 2,
          }}
        >
          {mainContent}
        </Box>
      ) : (
        // Versión de página completa (para PDF oculto, etc.)
        <PageBgBlock pad={id ? "0" : "2rem 9rem"}>{mainContent}</PageBgBlock>
      )}

      {children}

      {!id && !embedded ? (
                <Grid container spacing={2} justifyContent="center" sx={{ paddingBottom: "15px", paddingTop: "80px" }}>
          
          <Grid size={4} align="center">
                       <Box
                  sx={{
                    borderBottom: "2px solid black",
                    width: "60%",
                    margin: "0 auto",
                    paddingTop: "90px",
                  }}
                />
            <Typography variant="small" sx={{  }} >
              Firma
            </Typography>
          </Grid>
            
        </Grid>
      ) : null}
    </>
  );
}
